class AdvancedSlider {
  constructor(slider) {
    this.slider = slider;
    this.track = slider.querySelector('.slider-track');
    this.slides = Array.from(this.track.children);
    this.currentIndex = 0;

    this.settings = {
      arrows: slider.dataset.arrows === 'true',
      dots: slider.dataset.dots === 'true',
      draggable: slider.dataset.draggable === 'true',
      loop: slider.dataset.loop === 'true',
      autoplay: slider.dataset.autoplay === 'true',
      autoplayDelay: parseInt(slider.dataset.autoplaydelay || '4000'),
      gap: parseInt(slider.dataset.gap || '15'),
      slidesDesktop: parseFloat(slider.dataset.slidesDesktop || '3'),
      slidesTablet: parseFloat(slider.dataset.slidesTablet || '2'),
      slidesMobile: parseFloat(slider.dataset.slidesMobile || '1'),
      scrollDesktop: parseFloat(slider.dataset.scrollDesktop || '1'),
      scrollTablet: parseFloat(slider.dataset.scrollTablet || '2'),
      scrollMobile: parseFloat(slider.dataset.scrollMobile || '1')
    };

    this.touch = { startX: 0, active: false };
    this.autoplayTimer = null;

    this.setup();
  }

  setup() {
    this.setResponsive();
    this.createControls();
    this.attachEvents();
    this.update(true);
    if (this.settings.autoplay) this.startAutoplay();
  }

  setResponsive() {
    const width = window.innerWidth;
    if (width < 767) {
      this.slidesToShow = this.settings.slidesMobile;
      this.slidesToScroll = this.settings.scrollMobile;
      this.currentGap = 5; // 👈 Mobile gap
    } else if (width < 992) {
      this.slidesToShow = this.settings.slidesTablet;
      this.slidesToScroll = this.settings.scrollTablet;
      this.currentGap = this.settings.gap;
    } else {
      this.slidesToShow = this.settings.slidesDesktop;
      this.slidesToScroll = this.settings.scrollDesktop;
      this.currentGap = this.settings.gap;
    }

    if (this.settings.dots) {
      this.rebuildDots();
    }
  }

  createControls() {
    if (this.settings.arrows) {
      const prev = document.createElement('button');
      const next = document.createElement('button');
      prev.className = 'slider-arrow prev';
      next.className = 'slider-arrow next';
      prev.innerHTML = `<svg viewBox="0 0 40 40" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg" width="40" height="40"><path fill="transparent" stroke="hsl(0, 0%, 100%)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M22.5 12.5 15 20l7.5 7.5"/></svg>`;
      next.innerHTML = `<svg viewBox="0 0 40 40" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg" width="40" height="40"><path fill="transparent" stroke="hsl(0, 0%, 100%)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.5 12.5 25 20l-7.5 7.5"/></svg>`;
      prev.addEventListener('click', () => this.move(-1));
      next.addEventListener('click', () => this.move(1));
      this.slider.append(prev, next);
    }

    if (this.settings.dots) {
      this.dotsContainer = document.createElement('div');
      this.dotsContainer.className = 'slider-dots';
      this.slider.appendChild(this.dotsContainer);
      this.rebuildDots();
    }
  }

  rebuildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    const total = Math.ceil((this.slides.length - this.slidesToShow) / this.slidesToScroll) + 1;
    for (let i = 0; i < total; i++) {
      const dot = document.createElement('button');
      dot.className = 'dot';
      dot.addEventListener('click', () => {
        this.currentIndex = i * this.slidesToScroll;
        this.update();
        this.restartAutoplay();
      });
      this.dotsContainer.appendChild(dot);
    }
    this.updateDots();
  }

  attachEvents() {
    window.addEventListener('resize', () => {
      this.setResponsive();
      this.update(true);
    });

    if (this.settings.draggable) {
      this.track.addEventListener('mousedown', e => this.dragStart(e));
      this.track.addEventListener('touchstart', e => this.dragStart(e));
      this.track.addEventListener('mousemove', e => this.dragMove(e));
      this.track.addEventListener('touchmove', e => this.dragMove(e));
      this.track.addEventListener('mouseup', () => this.dragEnd());
      this.track.addEventListener('touchend', () => this.dragEnd());
      this.track.addEventListener('mouseleave', () => this.dragEnd());
    }
  }

  update(skipTransition = false) {
    const gap = this.currentGap;
    const visibleSlides = this.slidesToShow;
    const totalSlides = this.slides.length;

    const containerWidth = this.slider.clientWidth;
    const slideWidthPx = ((containerWidth - (gap * (visibleSlides - 1))) / visibleSlides - 1);

    this.slides.forEach(slide => {
      slide.style.flex = `0 0 ${slideWidthPx}px`;
      slide.style.width = `${slideWidthPx}px`;
    });

    const maxIndex = Math.max(0, Math.ceil((totalSlides - visibleSlides) / this.slidesToScroll));
    if (!this.settings.loop) {
      this.currentIndex = Math.max(0, Math.min(this.currentIndex, maxIndex));
    } else {
      if (this.currentIndex > maxIndex) this.currentIndex = 0;
      if (this.currentIndex < 0) this.currentIndex = maxIndex;
    }

    const translateX = (slideWidthPx + gap) * this.currentIndex * -1;

    this.track.style.transition = skipTransition ? 'none' : 'transform 0.5s ease';
    this.track.style.transform = `translateX(${translateX}px)`;
    this.track.style.gap = `${gap}px`;

    this.updateDots();
  }

  updateDots() {
    if (!this.dotsContainer) return;
    const dots = Array.from(this.dotsContainer.children);
    dots.forEach(dot => dot.classList.remove('active'));
    const activeDot = Math.round(this.currentIndex / this.slidesToScroll);
    if (dots[activeDot]) dots[activeDot].classList.add('active');
  }

  move(direction) {
    this.currentIndex += direction * this.slidesToScroll;
    this.update();
    this.restartAutoplay();
  }

  dragStart(e) {
    this.touch.active = true;
    this.touch.startX = e.touches ? e.touches[0].pageX : e.pageX;
  }

  dragMove(e) {
    if (!this.touch.active) return;
    const currentX = e.touches ? e.touches[0].pageX : e.pageX;
    const dist = this.touch.startX - currentX;
    if (Math.abs(dist) > 50) {
      this.move(dist > 0 ? 1 : -1);
      this.touch.active = false;
    }
  }

  dragEnd() {
    this.touch.active = false;
  }

  startAutoplay() {
    this.autoplayTimer = setInterval(() => this.move(1), this.settings.autoplayDelay);
  }

  restartAutoplay() {
    if (!this.settings.autoplay) return;
    clearInterval(this.autoplayTimer);
    this.startAutoplay();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.custom-slider').forEach(slider => new AdvancedSlider(slider));
});